import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getDeviceId } from '../../utils/deviceId';
import GeolocationCheck from '../GeolocationCheck';
import {
  DEFAULT_MALLORCA_AREAS,
  getMunicipioFromCoords,
  isPointInAreasFromCoords
} from '../../utils/municipios';

const ChatWidget = () => {
  const { isAuthenticated, user, loginAsAnonymous } = useAuth();
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [chatEnabled, setChatEnabled] = useState(true);
  const [adminOverride, setAdminOverride] = useState(false);
  const [chatPollIntervalSeconds, setChatPollIntervalSeconds] = useState(10);
  const [chatSettingsRefreshIntervalSeconds, setChatSettingsRefreshIntervalSeconds] = useState(30);
  const [wsConnected, setWsConnected] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(() => document.visibilityState === 'visible');
  const [messages, setMessages] = useState([]);
  const [, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [geoCheckRequested, setGeoCheckRequested] = useState(false);
  const deviceId = useMemo(() => getDeviceId(), []);
  const normalizedDeviceId = useMemo(
    () => (deviceId || '').replace(/^device[-_]/i, ''),
    [deviceId]
  );
  const isDriver = user?.user_type === 'conductor';
  const chatDeviceId = useMemo(
    () => (isDriver ? `chicho-${normalizedDeviceId}` : `web-${normalizedDeviceId}`),
    [normalizedDeviceId, isDriver]
  );
  const seededRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  const hasLoadedMessagesRef = useRef(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const idioma = (i18n?.language || 'es').toLowerCase();
  const chatPattern = `${process.env.PUBLIC_URL}/chat_pattern.png`;
  const PAYMENT_PREFIX = '__PAYMENT__:';

  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);
  const wsReconnectAttemptsRef = useRef(0);
  const DUPLICATE_WINDOW_MS = 10000;
  const pendingConfirmationRef = useRef(null);
  const pendingStepRef = useRef(null);
  const processedPaymentsRef = useRef(new Set());
  const pendingPaymentRef = useRef(null);
  const PAYMENT_HISTORY_KEY = 'support_chat_payment_orders';
  const PAYMENT_CONFIRMATION_KEY = 'support_chat_payment_confirmed';
  const paymentReturnRef = useRef(false);
  const paymentReturnHandledRef = useRef(new Set());

  const normalizeMatchText = (value) => {
    if (!value) return '';
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const extractCoordsFromText = (value) => {
    if (!value) return null;
    const match = value.match(/(-?\d{1,2}(?:[.,]\d+)?)\s*,\s*(-?\d{1,3}(?:[.,]\d+)?)/);
    if (!match) return null;
    const lat = parseFloat(match[1].replace(',', '.'));
    const lng = parseFloat(match[2].replace(',', '.'));
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
  };

  const getI18nList = (key, fallback = []) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? value : fallback;
  };

  const isSameMessage = useCallback((a, b) => {
    if (!a || !b) return false;
    if ((a.sender_type || a.senderType) !== (b.sender_type || b.senderType)) return false;
    if ((a.mensaje || a.text) !== (b.mensaje || b.text)) return false;
    if (a.local_only || b.local_only) return true;
    if (!a.fecha_envio || !b.fecha_envio) return false;
    const diff = Math.abs(new Date(a.fecha_envio).getTime() - new Date(b.fecha_envio).getTime());
    return diff <= DUPLICATE_WINDOW_MS;
  }, [DUPLICATE_WINDOW_MS]);

  const mergeMessages = useCallback((serverMessages, localMessages) => {
    const merged = [...serverMessages];
    const locals = (localMessages || []).filter((msg) => msg.local_only);
    locals.forEach((localMsg) => {
      const exists = merged.some((srv) => isSameMessage(srv, localMsg));
      if (!exists) {
        merged.push(localMsg);
      }
    });
    return merged;
  }, [isSameMessage]);

  const filterDuplicates = (items) => {
    const result = [];
    items.forEach((msg) => {
      const exists = result.some((prev) => isSameMessage(prev, msg));
      if (!exists) {
        result.push(msg);
      }
    });
    return result;
  };

  const isPaymentMessage = useCallback((msg) => {
    const text = msg?.mensaje || msg?.text || '';
    return text.startsWith(PAYMENT_PREFIX);
  }, [PAYMENT_PREFIX]);

  const submitPaymentForm = (payload) => {
    if (!payload?.url_pasarela || !payload?.form_params) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payload.url_pasarela;
    Object.entries(payload.form_params).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const canSend = useMemo(() => input.trim().length > 0, [input]);
  const headerTitle = useMemo(() => {
    if (user?.user_type === 'conductor') {
      return t('chat_widget_title_chicho');
    }
    return t('chat_widget_title_eusta');
  }, [t, user]);

  const getSenderLabel = () => {
    const isAnon = !isAuthenticated || user?.isAnonymous || (user?.user_type === 'cliente' && user?.isAnonymous);
    return isAnon
      ? t('chat_widget_anonymous_label', { deviceId })
      : (user?.email || `${user?.nombre || ''} ${user?.apellidos || ''}`.trim() || t('chat_widget_user_fallback_label'));
  };

  const sendMessage = async (messageText) => {
    const payload = {
      device_id: chatDeviceId,
      user_id: user?.user_id || null,
      idioma,
      sender_type: 'user',
      sender_label: getSenderLabel(),
      mensaje: messageText,
    };

    try {
      await fetch(`${apiBaseUrl}/api/chat-soporte/mensajes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setMessages((prev) => [...prev, {
        ...payload,
        id: `local-${Date.now()}`,
        fecha_envio: new Date().toISOString(),
        local_only: true,
      }]);
    } catch {
      // noop
    }
  };

  const sendBotMessage = useCallback(async (messageText) => {
    const payload = {
      device_id: chatDeviceId,
      user_id: null,
      idioma,
      sender_type: 'bot',
      sender_label: t('chat_widget_assistant_label'),
      mensaje: messageText,
    };

    try {
      await fetch(`${apiBaseUrl}/api/chat-soporte/mensajes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setMessages((prev) => [...prev, {
        ...payload,
        id: `local-bot-${Date.now()}`,
        fecha_envio: new Date().toISOString(),
        local_only: true,
      }]);
    } catch {
      // noop
    }
  }, [apiBaseUrl, chatDeviceId, idioma, t]);

  const getUsedPaymentOrders = useCallback(() => {
    try {
      const raw = localStorage.getItem(PAYMENT_HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  }, [PAYMENT_HISTORY_KEY]);

  const getConfirmedReservations = useCallback(() => {
    try {
      const raw = localStorage.getItem(PAYMENT_CONFIRMATION_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  }, [PAYMENT_CONFIRMATION_KEY]);

  const markPaymentOrderUsed = (orderId) => {
    if (!orderId) return;
    const used = getUsedPaymentOrders();
    used.add(orderId);
    try {
      localStorage.setItem(PAYMENT_HISTORY_KEY, JSON.stringify(Array.from(used)));
    } catch {
      // noop
    }
  };

  const markReservationConfirmed = useCallback((reservaId) => {
    if (!reservaId) return;
    const confirmed = getConfirmedReservations();
    confirmed.add(reservaId);
    try {
      localStorage.setItem(PAYMENT_CONFIRMATION_KEY, JSON.stringify(Array.from(confirmed)));
    } catch {
      // noop
    }
  }, [getConfirmedReservations]);

  const formatDateTime = useCallback((value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return date.toLocaleString(i18n?.language || 'es', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return date.toLocaleString();
    }
  }, [i18n?.language]);

  const buildReservationSummary = useCallback((reserva, reservaIdFallback) => {
    const origen = reserva?.origen_direccion || reserva?.origen_texto || reserva?.origen || '';
    const destino = reserva?.destino_direccion || reserva?.destino_texto || reserva?.destino || '';
    const fecha = formatDateTime(reserva?.fecha_hora_inicio);
    const reservaId = reserva?.id || reserva?.reserva_id || reservaIdFallback || '';
    const lines = [t('confirmacion_pago_ok')];
    if (reservaId) lines.push(`${t('confirmacion_id_reserva')}: **${reservaId}**`);
    if (origen) lines.push(`${t('confirmacion_origen')}: **${origen}**`);
    if (destino) lines.push(`${t('confirmacion_destino')}: **${destino}**`);
    if (fecha) lines.push(`${t('confirmacion_fecha_hora')}: **${fecha}**`);
    return lines.join('\n');
  }, [formatDateTime, t]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    messages.forEach((msg) => {
      if (!msg?.id || !isPaymentMessage(msg)) return;
      if (processedPaymentsRef.current.has(msg.id)) return;
      processedPaymentsRef.current.add(msg.id);
      const raw = msg?.mensaje || msg?.text || '';
      const payloadText = raw.slice(PAYMENT_PREFIX.length).trim();
      if (!payloadText) return;
      try {
        const payload = JSON.parse(payloadText);
        const used = getUsedPaymentOrders();
        if (payload?.order_id && used.has(payload.order_id)) return;
        if (!pendingPaymentRef.current) {
          pendingPaymentRef.current = payload;
          sendBotMessage(t('chat_payment_redirect_confirm'));
        }
      } catch (error) {
        console.error('[ChatWidget] Payment payload parse error:', error);
      }
    });
  }, [getUsedPaymentOrders, isPaymentMessage, messages, sendBotMessage, t]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const estado = (params.get('estado') || '').toLowerCase();
    const reservaId = params.get('reserva_id');
    if (estado !== 'ok' || !reservaId) return;
    const confirmed = getConfirmedReservations();
    if (confirmed.has(reservaId) || paymentReturnHandledRef.current.has(reservaId)) return;
    paymentReturnHandledRef.current.add(reservaId);
    paymentReturnRef.current = true;
    seededRef.current = true;

    const handlePaymentReturn = async () => {
      let token = localStorage.getItem('auth-token');
      if (!token && loginAsAnonymous) {
        try {
          await loginAsAnonymous();
          token = localStorage.getItem('auth-token');
        } catch {
          // noop
        }
      }

      const confirmPayload = {
        idioma,
        Ds_MerchantParameters: params.get('Ds_MerchantParameters'),
        Ds_Signature: params.get('Ds_Signature'),
        Ds_SignatureVersion: params.get('Ds_SignatureVersion'),
      };

      try {
        if (token) {
          await fetch(`${apiBaseUrl}/api/pagos/confirmar-pago/${reservaId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(confirmPayload),
          });
        }
      } catch {
        // noop
      }

      try {
        let reserva = null;
        if (token) {
          const reservaResponse = await fetch(`${apiBaseUrl}/api/reservas/${reservaId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (reservaResponse.ok) {
            reserva = await reservaResponse.json();
          }
        }

        markReservationConfirmed(reservaId);
        const summaryText = buildReservationSummary(reserva, reservaId);
        await sendBotMessage(summaryText);
        await sendBotMessage(t('chat_reserva_contacto'));
      } catch {
        markReservationConfirmed(reservaId);
        await sendBotMessage(t('confirmacion_pago_ok'));
        await sendBotMessage(t('chat_reserva_contacto'));
      }

      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch {
        // noop
      }
    };

    handlePaymentReturn();
  }, [apiBaseUrl, buildReservationSummary, getConfirmedReservations, idioma, loginAsAnonymous, markReservationConfirmed, sendBotMessage, t]);

  useEffect(() => {
    const isAnon = !isAuthenticated || user?.isAnonymous || (user?.user_type === 'cliente' && user?.isAnonymous);

    if (!hasLoadedMessagesRef.current) {
      prevMessageCountRef.current = messages.length;
      hasLoadedMessagesRef.current = true;
      return;
    }

    if (messages.length > prevMessageCountRef.current) {
      const latest = messages[messages.length - 1];
      const latestSender = latest?.sender_type || latest?.senderType;
      const isIncoming = latestSender && latestSender !== 'user';

      if (isAnon && isIncoming) {
        setIsOpen(true);
      }
    }

    prevMessageCountRef.current = messages.length;
  }, [isAuthenticated, messages, user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoadingMessages(true);
        const response = await fetch(`${apiBaseUrl}/api/chat-soporte/mensajes/${chatDeviceId}`);
        if (response.ok) {
          const data = await response.json();
          setMessages((prev) => mergeMessages(data, prev));
        }
      } catch {
        // noop
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();

    if (wsConnected || !isPageVisible) {
      return undefined;
    }

    const intervalMs = Math.max(8, Math.min(40, Number(chatPollIntervalSeconds) || 10)) * 1000;
    const intervalId = setInterval(fetchMessages, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [apiBaseUrl, chatDeviceId, chatPollIntervalSeconds, isPageVisible, mergeMessages, wsConnected]);

  useEffect(() => {
    if (!chatEnabled || !chatDeviceId) return;

    let shouldReconnect = true;
    let effectActive = true;

    const scheduleReconnect = () => {
      if (!shouldReconnect || !effectActive) return;
      const attempt = wsReconnectAttemptsRef.current;
      const delay = Math.min(30000, 1000 * (2 ** attempt));
      wsReconnectAttemptsRef.current = Math.min(attempt + 1, 10);
      wsReconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      const wsBaseUrl = apiBaseUrl.replace(/^http/, 'ws');
      const wsUrl = `${wsBaseUrl}/api/chat-soporte/ws/${chatDeviceId}`;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        if (!effectActive) {
          try {
            socket.close(1000, 'cleanup');
          } catch {
            // noop
          }
          return;
        }
        setWsConnected(true);
        wsReconnectAttemptsRef.current = 0;
      };

      socket.onmessage = (event) => {
        if (!effectActive) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'message' && payload?.data) {
            setMessages((prev) => mergeMessages([payload.data], prev));
          }
        } catch (error) {
          console.error('[ChatWidget] WebSocket parse error:', error);
        }
      };

      socket.onerror = (error) => {
        if (!effectActive) return;
        setWsConnected(false);
        console.error('[ChatWidget] WebSocket error:', error);
        try {
          if (socket.readyState === WebSocket.OPEN) {
            socket.close();
          }
        } catch {
          // noop
        }
      };

      socket.onclose = () => {
        if (!effectActive) return;
        setWsConnected(false);
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      effectActive = false;
      setWsConnected(false);
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
      }

      const socket = wsRef.current;
      wsRef.current = null;
      if (!socket) return;

      const readyState = socket.readyState;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;

      try {
        if (readyState === WebSocket.OPEN) {
          socket.close(1000, 'cleanup');
        } else if (readyState === WebSocket.CONNECTING) {
          socket.onopen = () => {
            try {
              socket.close(1000, 'cleanup');
            } catch {
              // noop
            }
          };
        }
      } catch {
        // noop
      }
    };
  }, [apiBaseUrl, chatDeviceId, chatEnabled, mergeMessages]);

  // Auto-greeting disabled: the customer should initiate the conversation.

  useEffect(() => {
    if (!isPageVisible) return undefined;

    const storedOpen = localStorage.getItem('support_chat_open');
    if (storedOpen === 'true') {
      setIsOpen(true);
    }

    const storedOverride = localStorage.getItem('support_chat_admin_override');
    if (storedOverride === 'true') {
      setAdminOverride(true);
    }

    const fetchChatSettings = () => {
      fetch(`${apiBaseUrl}/api/settings/public/chat-soporte`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (!data) return;
          const isEnabled = data.enabled !== false;
          const isPubliclyAvailable = data.publicly_available !== false;
          setChatEnabled(isEnabled && isPubliclyAvailable);
          setAdminOverride(Boolean(data.admin_override));
          const rawPoll = Number.parseInt(data.poll_interval_seconds, 10);
          const normalizedPoll = Number.isNaN(rawPoll) ? 10 : Math.max(8, Math.min(40, rawPoll));
          setChatPollIntervalSeconds(normalizedPoll);
          const rawSettingsRefresh = Number.parseInt(data.settings_refresh_interval_seconds, 10);
          const normalizedSettingsRefresh = Number.isNaN(rawSettingsRefresh) ? 30 : Math.max(8, Math.min(60, rawSettingsRefresh));
          setChatSettingsRefreshIntervalSeconds(normalizedSettingsRefresh);
        })
        .catch(() => {});
    };

    fetchChatSettings();
    const refreshMs = Math.max(8, Math.min(60, Number(chatSettingsRefreshIntervalSeconds) || 30)) * 1000;
    const intervalId = setInterval(fetchChatSettings, refreshMs);

    return () => clearInterval(intervalId);
  }, [apiBaseUrl, chatSettingsRefreshIntervalSeconds, isPageVisible]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'support_chat_admin_override') {
        setAdminOverride(event.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    localStorage.setItem('support_chat_open', String(isOpen));
  }, [isOpen]);

  const handleSend = async () => {
    if (!canSend || isSending) return;
    setIsSending(true);
    const userText = input.trim();

    const normalizedInput = normalizeMatchText(userText);
    const normalizedAnswer = normalizedInput;
    const affirmativeTokens = getI18nList('chat_affirmative_tokens', [
      'si',
      'sí',
      'vale',
      'ok',
      'okay',
      'de acuerdo',
      'perfecto',
      'claro',
      'yes',
      'yep',
      'yeah',
      'sure',
      'correct',
      'right',
      'of course',
      'oui',
      'ja',
      'da',
    ]).map((token) => normalizeMatchText(token));
    const isAffirmative = affirmativeTokens.some(
      (token) => normalizedAnswer === token || normalizedAnswer.startsWith(`${token} `)
    );
    const negativeTokens = getI18nList('chat_negative_tokens', [
      'no',
      'nope',
      'cancelar',
      'cancel',
      'negativo',
    ]).map((token) => normalizeMatchText(token));
    const isNegative = negativeTokens.some(
      (token) => normalizedAnswer === token || normalizedAnswer.startsWith(`${token} `)
    );
    const lastBotMessage = [...messages].reverse().find((msg) => msg.sender_type === 'bot');
    const lastBotText = normalizeMatchText(lastBotMessage?.mensaje || '');
    const includesAny = (text, phrases) => phrases.some((phrase) => text.includes(normalizeMatchText(phrase)));
    const askedCurrentLocationPhrases = getI18nList('chat_asked_current_location_phrases', [
      t('menu_reserva_ask_current_location'),
    ]);
    const awaitingPickupPhrases = getI18nList('chat_awaiting_pickup_phrases', [
      t('menu_reserva_ask_pickup_address'),
      t('chat_proximo_paso_recogida'),
    ]);
    const awaitingPickupConfirmPrefixes = getI18nList('chat_pickup_confirm_prefix', [
      'he entendido la direccion como',
    ]);
    const awaitingDestinationPhrases = getI18nList('chat_awaiting_destination_phrases', [
      t('chat_destination_prompt'),
      t('menu_reserva_ask_destination'),
    ]);
    const awaitingDestinationConfirmPrefixes = getI18nList('chat_destination_confirm_prefix', [
      'he entendido el destino como',
    ]);
    const askedCurrentLocation = includesAny(lastBotText, askedCurrentLocationPhrases);
    const awaitingPickupAddress = includesAny(lastBotText, awaitingPickupPhrases);
    const awaitingAddressConfirmation =
      pendingConfirmationRef.current === 'pickup' ||
      includesAny(lastBotText, awaitingPickupConfirmPrefixes);
    const awaitingDestination =
      pendingStepRef.current === 'destination' ||
      includesAny(lastBotText, awaitingDestinationPhrases);
    const awaitingDestinationConfirmation =
      pendingConfirmationRef.current === 'destination' ||
      includesAny(lastBotText, awaitingDestinationConfirmPrefixes);
    const hasConfirmedDestination = (() => {
      for (let i = 0; i < messages.length; i += 1) {
        const msg = messages[i];
        if (msg.sender_type !== 'bot' || !msg.mensaje) continue;
        const botText = normalizeMatchText(msg.mensaje);
        if (!awaitingDestinationConfirmPrefixes.some((prefix) => botText.includes(normalizeMatchText(prefix)))) {
          continue;
        }
        for (let j = i + 1; j < messages.length; j += 1) {
          const userMsg = messages[j];
          if (userMsg.sender_type !== 'user' || !userMsg.mensaje) continue;
          const answer = normalizeMatchText(userMsg.mensaje);
          if (isAffirmative && answer === normalizedAnswer) {
            return true;
          }
          if (answer && affirmativeTokens.some((token) => answer === token || answer.startsWith(`${token} `))) {
            return true;
          }
          break;
        }
      }
      return false;
    })();
    const currentLocationKeywords = getI18nList('chat_current_location_keywords', [
      'ubicación actual',
      'ubicacion actual',
      'mi ubicación actual',
      'mi ubicacion actual',
      'current location',
      'my current location',
      'my location',
      'location now',
      'my position',
      'here',
      'this location',
    ]).map((keyword) => normalizeMatchText(keyword));
    const wantsCurrentLocation = currentLocationKeywords.some((keyword) => normalizedInput.includes(keyword));
    const parsedCoords = extractCoordsFromText(userText);
    const quickLocationTokens = getI18nList('chat_current_location_quick_tokens', [
      'current',
      'here',
      'this',
      'location',
    ]).map((keyword) => normalizeMatchText(keyword));
    const wantsCurrentLocationQuick = askedCurrentLocation && quickLocationTokens.includes(normalizedAnswer);


    const getCurrentPosition = () => new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('chat_geolocation_not_supported')));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

    const geocodeAddress = (address) => new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        reject(new Error(t('chat_geocoder_not_available')));
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry?.location;
          const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
          const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;
          if (typeof lat === 'number' && typeof lng === 'number') {
            resolve({ lat, lng, formattedAddress: results[0].formatted_address });
            return;
          }
        }
        reject(new Error(t('chat_geocoding_failed')));
      });
    });

    let finalUserText = userText;
    let skipAiResponse = false;

    if (pendingPaymentRef.current) {
      if (isAffirmative) {
        await sendMessage(userText);
        const payload = pendingPaymentRef.current;
        pendingPaymentRef.current = null;
        const used = getUsedPaymentOrders();
        if (payload?.order_id && used.has(payload.order_id)) {
          await sendBotMessage(t('chat_payment_already_used'));
        } else {
          if (payload?.reserva_id || payload?.order_id) {
            try {
              localStorage.setItem('support_chat_last_payment', JSON.stringify({
                reserva_id: payload?.reserva_id || null,
                order_id: payload?.order_id || null,
                metodo: payload?.metodo || null,
                timestamp: Date.now(),
              }));
            } catch {
              // noop
            }
          }
          submitPaymentForm(payload);
          markPaymentOrderUsed(payload?.order_id);
        }
        skipAiResponse = true;
      } else if (isNegative) {
        await sendMessage(userText);
        pendingPaymentRef.current = null;
        await sendBotMessage(t('chat_payment_redirect_cancelled'));
        skipAiResponse = true;
      }
    }

    if (!skipAiResponse && isAffirmative && awaitingAddressConfirmation) {
      await sendMessage(userText);
      pendingConfirmationRef.current = null;
      await sendBotMessage(t('chat_destination_prompt'));
      pendingStepRef.current = 'destination';
      skipAiResponse = true;
    } else if (!skipAiResponse && isAffirmative && awaitingDestinationConfirmation) {
      await sendMessage(userText);
      pendingConfirmationRef.current = null;
      await sendBotMessage(t('menu_reserva_ask_payment'));
      skipAiResponse = true;
    } else if (!skipAiResponse && ((isAffirmative && askedCurrentLocation) || wantsCurrentLocation || wantsCurrentLocationQuick || parsedCoords)) {
      setGeoCheckRequested(true);
      try {
        const coords = parsedCoords || (() => null)();
        const position = coords ? null : await getCurrentPosition();
        const latitude = coords ? coords.lat : position?.coords?.latitude;
        const longitude = coords ? coords.lng : position?.coords?.longitude;
        if (typeof latitude === 'number' && typeof longitude === 'number') {
          const locationLabel = t('chat_current_location_label');
          const locationText = `${locationLabel}: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          finalUserText = userText.includes(locationText) ? userText : `${userText}\n${locationText}`;
          await sendMessage(finalUserText);

          const municipio = await getMunicipioFromCoords(latitude, longitude);
          if (municipio) {
            if (!hasConfirmedDestination) {
              const municipioText = t('chat_pickup_municipio_ok', { municipio });
              await sendBotMessage(municipioText);
              pendingStepRef.current = 'destination';
              skipAiResponse = true;
            }
          } else {
            const invalidText = t('chat_pickup_outside_area');
            await sendBotMessage(invalidText);
            pendingStepRef.current = null;
            skipAiResponse = true;
          }
        } else {
          await sendMessage(userText);
        }
      } catch {
        await sendMessage(userText);
      } finally {
        setGeoCheckRequested(false);
      }
    } else if (!skipAiResponse) {
      await sendMessage(userText);
    }
    setInput('');

    if (!adminOverride && !skipAiResponse) {
      if (awaitingPickupAddress && userText.length > 5) {
        try {
          const coords = parsedCoords || extractCoordsFromText(userText);
          if (coords) {
            const municipio = await getMunicipioFromCoords(coords.lat, coords.lng);
            if (municipio) {
              const locationLabel = t('chat_current_location_label');
              const displayAddress = `${locationLabel}: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
              const confirmText = t('chat_pickup_confirm', { direccion: displayAddress });
              await sendBotMessage(confirmText);
              pendingConfirmationRef.current = 'pickup';
            } else {
              const invalidText = t('chat_pickup_invalid');
              await sendBotMessage(invalidText);
              pendingConfirmationRef.current = null;
            }
            skipAiResponse = true;
          } else {
            const { lat, lng, formattedAddress } = await geocodeAddress(userText);
            const municipio = await getMunicipioFromCoords(lat, lng);
            if (municipio) {
              const displayAddress = formattedAddress || userText;
              const confirmText = t('chat_pickup_confirm', { direccion: displayAddress });
              await sendBotMessage(confirmText);
              pendingConfirmationRef.current = 'pickup';
            } else {
              const invalidText = t('chat_pickup_invalid');
              await sendBotMessage(invalidText);
              pendingConfirmationRef.current = null;
            }
            skipAiResponse = true;
          }
        } catch {
          const fallbackText = t('chat_pickup_fallback');
          await sendBotMessage(fallbackText);
          pendingConfirmationRef.current = null;
          skipAiResponse = true;
        }
      } else if (awaitingDestination && userText.length > 5) {
        try {
          const { lat, lng, formattedAddress } = await geocodeAddress(userText);
          const isValid = await isPointInAreasFromCoords(lat, lng, DEFAULT_MALLORCA_AREAS);
          if (isValid) {
            const displayAddress = formattedAddress || userText;
            const confirmText = t('chat_destination_confirm', { destino: displayAddress });
            await sendBotMessage(confirmText);
            pendingConfirmationRef.current = 'destination';
            pendingStepRef.current = null;
          } else {
            const invalidText = t('chat_destination_invalid');
            await sendBotMessage(invalidText);
            pendingConfirmationRef.current = null;
            pendingStepRef.current = 'destination';
          }
          skipAiResponse = true;
        } catch {
          const fallbackText = t('chat_destination_fallback');
          await sendBotMessage(fallbackText);
          pendingConfirmationRef.current = null;
          pendingStepRef.current = 'destination';
          skipAiResponse = true;
        }
      }
    }

    if (!adminOverride && !skipAiResponse) {
      try {
          const aiResponse = await fetch(`${apiBaseUrl}/api/chat-soporte/ai-respuesta`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            device_id: chatDeviceId,
            user_id: user?.user_id || null,
            idioma,
            mensaje: finalUserText,
          }),
        });

        if (aiResponse.status === 204) {
          setIsSending(false);
          return;
        }

        if (aiResponse.ok) {
          const data = await aiResponse.json();
          setMessages((prev) => [...prev, data]);
        } else {
          console.error('[ChatWidget] Error AI:', aiResponse.status);
        }
      } catch (error) {
        console.error('[ChatWidget] Error AI:', error);
      }
    }
    setIsSending(false);
  };

  const handleClearChat = async () => {
    if (isClearing) return;
    setIsClearing(true);
    try {
      await fetch(`${apiBaseUrl}/api/chat-soporte/mensajes/${chatDeviceId}`, {
        method: 'DELETE',
      });
    } catch {
      // noop
    }
    seededRef.current = false;
    setMessages([]);
    setIsClearing(false);
  };

  if (!chatEnabled || user?.user_type === 'administrador') {
    return null;
  }

  return (
    <Box className="chat-widget-container">
      {geoCheckRequested && (
        <GeolocationCheck
          onGeolocationGranted={() => setGeoCheckRequested(false)}
          onGeolocationDenied={() => setGeoCheckRequested(false)}
        />
      )}
      {!isOpen ? (
        <Button
          className="chat-widget-fab"
          onClick={() => setIsOpen(true)}
          aria-label={t('chat_widget_open_label')}
        >
          💬
        </Button>
      ) : (
        <Box className="chat-widget-panel">
          <HStack className="chat-widget-header" justify="space-between">
            <Text fontWeight="bold">{headerTitle}</Text>
            <HStack spacing={1}>
              <IconButton
                size="sm"
                aria-label={t('chat_widget_clear_label')}
                onClick={handleClearChat}
                isDisabled={isClearing}
                isLoading={isClearing}
                icon={<span>🗑️</span>}
              />
              <IconButton
                size="sm"
                aria-label={t('chat_widget_close_label')}
                onClick={() => setIsOpen(false)}
                icon={<span>✕</span>}
              />
            </HStack>
          </HStack>

          <VStack
            className="chat-widget-messages"
            spacing={3}
            align="stretch"
            style={{
              backgroundImage: `url(${chatPattern})`,
              backgroundRepeat: 'repeat',
              backgroundSize: 'auto',
              backgroundPosition: 'top left',
            }}
          >
            {filterDuplicates(messages).filter((msg) => !isPaymentMessage(msg)).map((msg) => {
              const isUser = msg.sender_type === 'user' || msg.senderType === 'user';
              return (
                <Box
                  key={msg.id}
                  alignSelf={isUser ? 'flex-end' : 'flex-start'}
                  px={3}
                  py={2}
                  borderRadius="lg"
                  maxW="85%"
                  fontSize="sm"
                  className={`chat-widget-message ${isUser ? 'chat-widget-message-user' : 'chat-widget-message-bot'}`}
                >
                  {msg.mensaje || msg.text}
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </VStack>

          <HStack className="chat-widget-input" spacing={2}>
            <Input
              size="sm"
              placeholder={t('chat_widget_placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isSending) {
                    handleSend();
                  }
                }
              }}
            />
            <Button
              size="sm"
              colorScheme="teal"
              onClick={handleSend}
              isDisabled={!canSend || isSending}
            >
              {t('chat_widget_send')}
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default ChatWidget;
